import { useQuery, useQueryClient } from '@tanstack/react-query';
import { portalAPI } from '@/lib/api/portal';
import type { Availability } from '@/types';

/**
 * Hook to fetch and cache event data
 */
export function useEvent() {
  return useQuery({
    queryKey: ['event'],
    queryFn: () => portalAPI.getEvent(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch tournaments for the event
 */
export function useTournaments(params?: { status?: string; visible?: boolean }) {
  return useQuery({
    queryKey: ['tournaments', params],
    queryFn: () => portalAPI.getTournaments(params),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to fetch a single tournament
 */
export function useTournament(id: string) {
  return useQuery({
    queryKey: ['tournament', id],
    queryFn: () => portalAPI.getTournament(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch activities for the event
 */
export function useActivities(params?: { type?: string; status?: string; visible?: boolean }) {
  return useQuery({
    queryKey: ['activities', params],
    queryFn: () => portalAPI.getActivities(params),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to fetch addons for the event
 */
export function useAddons(params?: { category?: string; isActive?: boolean; featured?: boolean }) {
  return useQuery({
    queryKey: ['addons', params],
    queryFn: () => portalAPI.getAddons(params),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to fetch special events
 */
export function useSpecialEvents(params?: { status?: string; visible?: boolean }) {
  return useQuery({
    queryKey: ['special-events', params],
    queryFn: () => portalAPI.getSpecialEvents(params),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to fetch flow configuration
 */
export function useFlowConfig() {
  return useQuery({
    queryKey: ['flowConfig'],
    queryFn: () => portalAPI.getFlowConfig(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to fetch styling configuration
 */
export function useStylingConfig() {
  return useQuery({
    queryKey: ['stylingConfig'],
    queryFn: () => portalAPI.getStylingConfig(),
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * Hook to fetch real-time availability
 */
export function useAvailability(refetchInterval = 30000) {
  return useQuery({
    queryKey: ['availability'],
    queryFn: () => portalAPI.getAvailability(),
    refetchInterval, // Refetch every 30 seconds by default
    staleTime: 1000 * 10, // 10 seconds
  });
}

/**
 * Hook to monitor availability for specific items
 */
export function useAvailabilityMonitor(
  itemType: 'tournament' | 'activity' | 'addon',
  itemId: string
) {
  const { data: availability, ...rest } = useAvailability();

  const itemAvailability = availability?.[`${itemType}s` as keyof Availability]?.[itemId];

  return {
    ...rest,
    data: itemAvailability,
    isAvailable: itemAvailability ? (itemAvailability.available ?? Infinity) > 0 : true,
    spotsRemaining: itemAvailability?.available ?? null,
  };
}

/**
 * Hook to prefetch common data
 */
export function usePrefetchData() {
  const queryClient = useQueryClient();

  return {
    prefetchEvent: () => queryClient.prefetchQuery({
      queryKey: ['event'],
      queryFn: () => portalAPI.getEvent(),
    }),
    prefetchTournaments: () => queryClient.prefetchQuery({
      queryKey: ['tournaments', { status: 'open', visible: true }],
      queryFn: () => portalAPI.getTournaments({ status: 'open', visible: true }),
    }),
    prefetchAddons: () => queryClient.prefetchQuery({
      queryKey: ['addons', { isActive: true }],
      queryFn: () => portalAPI.getAddons({ isActive: true }),
    }),
    prefetchFlowConfig: () => queryClient.prefetchQuery({
      queryKey: ['flowConfig'],
      queryFn: () => portalAPI.getFlowConfig(),
    }),
  };
}
